import * as React from "react";

/** Shared animation duration in ms for overlay enter/exit transitions. */
export const OVERLAY_ANIMATION_DURATION_MS = 300;

/**
 * Value exposed by the widget overlay context.
 */
export type WidgetOverlayContextValue = {
  /** The current overlay content, or null if no overlay is active. */
  overlayContent: React.ReactNode | null;
  /** Whether the overlay is currently visible (controls animation). */
  isOverlayVisible: boolean;
  /** Shows an overlay with the given content. */
  showOverlay: (params: { content: React.ReactNode }) => void;
  /** Hides the overlay with an exit animation, then clears content. */
  hideOverlay: () => void;
};

const WidgetOverlayContext = React.createContext<WidgetOverlayContextValue | null>(null);
WidgetOverlayContext.displayName = "WidgetOverlayContext";

/**
 * Provider that manages the overlay state for the widget frame.
 *
 * Supports showing arbitrary content as an animated overlay inside the
 * widget. Only one overlay can be active at a time.
 */
export function WidgetOverlayProvider({ children }: { children: React.ReactNode }) {
  const [overlayContent, setOverlayContent] = React.useState<React.ReactNode | null>(null);
  const [isOverlayVisible, setIsOverlayVisible] = React.useState(false);
  const closeTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const rafRef = React.useRef<number | null>(null);

  const showOverlay = React.useCallback(({ content }: { content: React.ReactNode }) => {
    // Cancel any pending hide timer
    if (closeTimerRef.current !== null) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
    // Cancel any pending rAF
    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current);
    }
    setOverlayContent(content);
    // Use rAF to ensure content is mounted before triggering animation
    rafRef.current = requestAnimationFrame(() => {
      setIsOverlayVisible(true);
      rafRef.current = null;
    });
  }, []);

  const hideOverlay = React.useCallback(() => {
    setIsOverlayVisible(false);
    // Delay clearing content to allow exit animation
    closeTimerRef.current = setTimeout(() => {
      setOverlayContent(null);
      closeTimerRef.current = null;
    }, OVERLAY_ANIMATION_DURATION_MS);
  }, []);

  // Cleanup pending timers and rAF on unmount
  React.useEffect(() => {
    return () => {
      if (closeTimerRef.current !== null) {
        clearTimeout(closeTimerRef.current);
      }
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
      }
    };
  }, []);

  const value = React.useMemo<WidgetOverlayContextValue>(
    () => ({ overlayContent, isOverlayVisible, showOverlay, hideOverlay }),
    [overlayContent, isOverlayVisible, showOverlay, hideOverlay],
  );

  return (
    <WidgetOverlayContext.Provider value={value}>
      {children}
    </WidgetOverlayContext.Provider>
  );
}

/**
 * Hook to consume the widget overlay context.
 *
 * Must be called inside a WidgetOverlayProvider. Throws if used outside.
 */
export function useWidgetOverlay(): WidgetOverlayContextValue {
  const ctx = React.useContext(WidgetOverlayContext);
  if (!ctx) {
    throw new Error("useWidgetOverlay must be used within a WidgetOverlayProvider");
  }
  return ctx;
}

/**
 * Optional hook that returns undefined when outside the provider.
 */
export function useWidgetOverlayOptional(): WidgetOverlayContextValue | undefined {
  return React.useContext(WidgetOverlayContext) ?? undefined;
}

/**
 * Props forwarded from dnd-kit's useDraggable to activate drag on an element.
 */
export type DragHandleProps = {
  /** Ref callback from dnd-kit's useDraggable (setActivatorNodeRef). */
  ref: React.RefCallback<HTMLElement>;
  /** Pointer event listeners from dnd-kit's useDraggable. */
  listeners: Record<string, React.EventHandler<any>> | undefined;
};

const DragHandleContext = React.createContext<DragHandleProps | null>(null);
DragHandleContext.displayName = "DragHandleContext";

/**
 * Provider that exposes the widget's drag handle props to descendants.
 *
 * Wrap overlay content with this so inner components can become drag activators.
 */
export function DragHandleProvider({
  children,
  dragHandleProps,
}: {
  children: React.ReactNode;
  dragHandleProps: DragHandleProps;
}) {
  return (
    <DragHandleContext.Provider value={dragHandleProps}>
      {children}
    </DragHandleContext.Provider>
  );
}

/**
 * Hook to consume drag handle props from the nearest DragHandleProvider.
 *
 * Returns null when called outside a provider.
 */
export function useDragHandle(): DragHandleProps | null {
  return React.useContext(DragHandleContext);
}
